import { Progress } from 'antd';
import { CancelButton, ConfirmButton } from 'components/common/DefaultFormButton';
import { INPUT_TYPE } from 'components/common/Input/types';
import InputFieldsGenerator from 'components/InputFieldsGenerator';
import { InputProps } from 'components/InputFieldsGenerator/types';
import { ERRORS } from 'constants/notify';
import { Form, Formik } from 'formik';
import useUploadTransactionAppointmentFile from 'hooks/useUploadTransactionAppointmentFile';
import React from 'react';
import { useSelector } from 'react-redux';
import AuthSelectors from 'redux/auth/auth.selectors';
import { IAppointment } from 'types/appointments';
import { GeneralFunction } from 'types/common';
import { DocumentSubType } from 'types/file';
import { ITransaction } from 'types/transaction';
import { defaultNotification, getFileViewUrl } from 'utils';
import * as Yup from 'yup';

const schema = {
  scannedFiles: Yup.array().min(1, 'Need at least 1 document').required('Need at least 1 document'),
};

const UploadDocumentForm: React.FC<{
  appointment: IAppointment;
  transaction?: ITransaction;
  closeModal?: GeneralFunction;
  refetch?: GeneralFunction;
}> = ({ appointment, closeModal, transaction, refetch }) => {
  const token = useSelector(AuthSelectors.getAccessToken);
  const { uploadDocument, uploadPercentage, loading } = useUploadTransactionAppointmentFile({ showPercentage: true });
  const { scannedFiles } = appointment;

  const formFields: InputProps[] = [
    {
      label: 'Upload Signed Documents',
      name: 'scannedFiles',
      type: INPUT_TYPE.FILE,
      inputSettings: {
        accept: 'image/*, .pdf',
      },
    },
  ];

  const initValues = {
    scannedFiles:
      scannedFiles?.map((f) => ({
        uid: f.id,
        name: f.name,
        url: getFileViewUrl(f.id, token || '', f.name),
        status: 'done',
      })) || [],
  };

  const onSubmit = (v: { scannedFiles: any }) => {
    uploadDocument({
      callbacks: {
        fail: () => {
          defaultNotification({ failure: ERRORS.DEFAULT_MUTATION_ERROR });
        },
        success: () => {
          defaultNotification({ success: 'Upload documents successfully!' });
          closeModal?.();
          refetch?.();
        },
      },
      files: v.scannedFiles,
      transactionId: transaction?.id,
      appointmentIds: [appointment.id],
      subType: DocumentSubType.SCANNED,
      existedFiles: appointment?.scannedFiles || [],
    });
  };

  return (
    <div>
      <Formik onSubmit={onSubmit} enableReinitialize initialValues={initValues} validationSchema={Yup.object(schema)}>
        <Form>
          <InputFieldsGenerator formFields={formFields} />
          {loading && (
            <div style={{ padding: '10px 16px' }}>
              <Progress percent={uploadPercentage} strokeColor='#038fde' />
            </div>
          )}
          <div className='flex'>
            <div>
              <ConfirmButton htmlType='submit'>Save</ConfirmButton>
            </div>
            <div className='ml-3'>
              <CancelButton onClick={closeModal}>Cancel</CancelButton>
            </div>
          </div>
        </Form>
      </Formik>
    </div>
  );
};

export default React.memo(UploadDocumentForm);
